import React from "react";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";

import { SubmitButton } from "components/common/buttons/SubmitButton";
import FullPageLoader from "components/common/FullPageLoader";
import CancelModal from "components/appointment/CancelModal";
import DetailCard from "components/appointment/view/DetailCard";
import AppointmentViewHeader from "components/appointment/view/AppointmentViewHeader";
import ActionBar from "components/appointment/view/ActionBar";
import {
  appointmentRows,
  otherDetailRows,
  locationRows,
  requesterRows,
  vendorRows,
  requirementRows,
  dynamicFieldRows,
  checkInOutRows,
  feedbackRows,
  videoRows,
} from "components/appointment/view/sections";
import { setActionAssign } from "slices/actionButtonSlice";
import {
  fetchMainAccountAppointmentLogs,
  fetchMainAccountAppointmentView,
} from "slices/mainAccount/mainAccountAppointmentSlice";
import LogsTable from "./LogsTable";

const AppointmentView = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const dispatch = useDispatch();

  const [item, setItem] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [logs, setLogs] = React.useState([]);
  const [cancelModal, setCancelModal] = React.useState(false);

  const callViewApi = () => {
    dispatch(
      fetchMainAccountAppointmentView(id, (success, message, data) => {
        setLoading(false);
        if (success) setItem(data);
        else console.log(message);
      })
    );
  };

  const fetchLogs = () => {
    dispatch(
      fetchMainAccountAppointmentLogs(id, (success, message, data) => {
        if (success) setLogs(data);
        else console.log(message);
      })
    );
  };

  React.useEffect(() => {
    setLoading(true);
    callViewApi();
    fetchLogs();
  }, [id]);

  const handleSubmit = (data, end_point, callBack) => {
    setLoading(true);
    dispatch(
      setActionAssign(id, data, end_point, (success, message) => {
        callBack && callBack();
        if (success) {
          setCancelModal(false);
          callViewApi();
          fetchLogs();
          toast.success(message, { className: "successToast" });
        } else {
          toast.error(message, { className: "errorToast" });
        }
        setLoading(false);
      })
    );
  };

  const ClientTimezone = item?.accounts?.timezone;
  const AppointmentTimezone = item?.appointment_details?.timezone;
  const rawDatetime = item?.datetime;

  // Date in the appointment's own timezone so late-night appointments keep their day.
  const formattedDate = AppointmentTimezone?.name
    ? moment.tz(rawDatetime, AppointmentTimezone.name).format("MMM D, YYYY")
    : moment(rawDatetime).format("MMM D, YYYY");

  // Time shown in the client's timezone.
  const formattedClientTime = (() => {
    if (!rawDatetime) return t("common.nA");
    if (!AppointmentTimezone?.name) return moment(rawDatetime).format("hh:mm A");
    const m = ClientTimezone?.name
      ? moment.tz(rawDatetime, AppointmentTimezone.name).tz(ClientTimezone.name)
      : moment.tz(rawDatetime, AppointmentTimezone.name);
    const abbr = m.format("z");
    const offset = m.format("Z");
    return `${m.format("hh:mm A")} ${abbr ? `(${abbr} / GMT${offset})` : `(GMT${offset})`}`;
  })();

  const notDone = item?.status !== "completed" && item?.status !== "cancelled";
  const isVri = item?.type === "Video Remote Interpretation";
  const dynamicRows = dynamicFieldRows(item, t);

  const whenRows = [
    { label: t("table.date"), value: formattedDate },
    { label: t("appointmentView.clientTime"), value: formattedClientTime },
  ];

  return (
    <div className="content appt-view">
      {loading && <FullPageLoader />}

      <AppointmentViewHeader item={item} />

      {notDone && (
        <ActionBar>
          <SubmitButton buttonType="button" className="tomatoRed" btnText={t("common.cancel")} handleSubmit={() => setCancelModal(true)} />
        </ActionBar>
      )}

      <div className="appt-grid">
        <div className="appt-col">
          <DetailCard title={t("appointmentView.cards.details")} rows={appointmentRows(item, t, { when: whenRows })} />
          <DetailCard title={t("appointmentView.cards.vendor")} rows={vendorRows(item, t, { contact: false })} />
        </div>
        <div className="appt-col">
          <DetailCard title={t("appointmentView.cards.location")} rows={locationRows(item, t)} />
          <DetailCard title={t("appointmentView.cards.requester")} rows={requesterRows(item, t)} />
          <DetailCard title={t("appointmentView.cards.requirements")} rows={requirementRows(item, t)} />
        </div>
        <DetailCard className="appt-grid__full" title={t("appointmentView.cards.otherDetails")} columns={2} rows={otherDetailRows(item, t, { adminNotes: false })} />
        {dynamicRows.length > 0 && (
          <DetailCard className="appt-grid__full" title={t("appointmentView.cards.customFields")} columns={2} rows={dynamicRows} />
        )}
        <DetailCard className="appt-grid__full" title={t("appointmentView.cards.checkInOut")} columns={2} rows={checkInOutRows(item, t)} />
        {isVri && <DetailCard title={t("appointmentView.cards.video")} rows={videoRows(item, t)} />}
        {item?.status === "completed" && item?.feedback && (
          <DetailCard className="appt-grid__full" title={t("appointmentView.cards.feedback")} rows={feedbackRows(item, t)} />
        )}
      </div>

      <section className="appt-card appt-logs">
        <h3 className="appt-card__title">{t("appointmentDetail.appointmentLogs", { defaultValue: "Appointment Logs" })}</h3>
        <div className="appt-card__body">
          <LogsTable logs={logs} />
        </div>
      </section>

      <CancelModal
        isOpen={cancelModal}
        onClose={() => setCancelModal(false)}
        onConfirm={handleSubmit}
        appointmentId={id}
        item={item}
        loader={loading}
      />
    </div>
  );
};

export default AppointmentView;
